import { Dispatch, useCallback, useEffect, useState } from "react";
import { ILoaded } from "sonobello.utilities.react";
import { Nullable } from "sonobello.utilities.react.mui";

import { OAuthConfiguration } from "../Types/OAuthConfiguration";

export enum OAuthError {
  /** The authentication window failed to open. */
  WindowDidNotOpen,
  /** The authentication window was interrupted or closed prior to response completion. */
  WindowClosed,
  /** The authentication request received a response lacking an identifiable oauth code. */
  CodeNotReturned,
  /** The authentication request lacked a response when the configured time-out was reached. */
  Timeout,
  /** An error was returned by the server. */
  ReturnedError
}

/** A mapping from a reported oauth error to a user-friendly error message. */
export const oAuthErrorMessageMap: Record<OAuthError, string> = {
  [OAuthError.CodeNotReturned]: "Authentication code could not be extracted from the response.",
  [OAuthError.WindowClosed]:
    "The Authentication Pop-Up was closed before authentication was complete. Please try again.",
  [OAuthError.WindowDidNotOpen]: "Pop-Ups may be blocked, please allow Pop Ups for this site and try again.",
  [OAuthError.ReturnedError]: "",
  [OAuthError.Timeout]: "Authentication was not completed within a reasonable amount of time. Please try again."
};

export interface IUseGetOAuthCodeResponse extends ILoaded {
  /** The code identifying the error scenario that occurred during the request process. */
  errorCode: Nullable<OAuthError>;
  /** The code from the response payload of the most recent web request. */
  code: Nullable<string>;
  /** Dispatches a web request to the OAuth endpoint defined by the given configuration. */
  sendRequest: Dispatch<OAuthConfiguration>;
}

const useGetOAuthCode = (): IUseGetOAuthCodeResponse => {
  const [url, setUrl] = useState<Nullable<string>>(null);
  const sendRequest = useCallback((config: OAuthConfiguration) => {
    setUrl(config.BuildUrl());
  }, []);
  const [errorCode, setErrorCode] = useState<Nullable<OAuthError>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState<Nullable<string>>(null);

  useEffect(() => {
    if (!url) return;

    const newWindow = window.open(url, "_blank");

    if (!newWindow || newWindow.closed) {
      setErrorCode(OAuthError.WindowDidNotOpen);
      setIsLoading(false);
    } else if (newWindow) {
      let ticks = 0;
      const authInterval = process.env.REACT_APP_AUTH_INTERVAL;
      const authTimeout = process.env.REACT_APP_AUTH_TIMEOUT;

      const intervalDuration = authInterval ? Number(authInterval) : 500;
      const maxSeconds = authTimeout ? Number(authTimeout) : 300;
      const maxTicks = (maxSeconds * 1000) / intervalDuration;

      const intervalId = setInterval(() => {
        if (newWindow.closed) {
          setErrorCode(OAuthError.WindowClosed);
          clearInterval(intervalId);
          setIsLoading(false);
        }

        try {
          // Check whether the window is now at our redirect Uri.
          const windowUri = `${newWindow.location.origin}${newWindow.location.pathname}`;

          if (windowUri === process.env.REACT_APP_OAUTH_REDIRECT) {
            const urlSearch = new URLSearchParams(newWindow.location.search);

            // Get the "code" value from the redirected window location.
            const code = urlSearch.get("code");

            if (code) {
              // If we got the code trigger the exchange.
              setCode(code);
              // Close the new window since we now have the value.
              newWindow.close();
            } else {
              const errorDescription = urlSearch.get("error_description");
              if (errorDescription)
                console.error(`OAuth code was not found with error: "${decodeURIComponent(errorDescription)}"`);
              else setErrorCode(OAuthError.CodeNotReturned);
            }

            // Clear variables to reset the form.
            clearInterval(intervalId);
            setIsLoading(false);
          }
        } catch (e) {
          // During the time what the user is on the InContact login page, this will throw.
        }
        ticks++;

        // End if we have exceeded the timeout.
        if (ticks > maxTicks) {
          setErrorCode(OAuthError.Timeout);
          clearInterval(intervalId);
          setIsLoading(false);
        }
      }, intervalDuration);
    } else {
      // I don't think we can ever get here but just to be safe.
      setIsLoading(false);
    }
  }, [url]);

  return { isLoading, errorCode, code, sendRequest };
};

export default useGetOAuthCode;
